import React, { useState, useEffect } from 'react';
import { 
  DocumentTextIcon,
  CheckCircleIcon,
  WrenchScrewdriverIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  ListBulletIcon
} from '@heroicons/react/24/outline';

import KPICard from '../components/KPICard';
import ChartContainer from '../components/ChartContainer';
import PieChart from '../components/charts/PieChart';
import LineChart from '../components/charts/LineChart';
import UpdateNotification from '../components/UpdateNotification';
import dataService from '../services/dataService';
import backendApiService from '../services/backendApiService';
import exportService from '../services/exportService';
import pdfExportService from '../services/pdfExportService';

const Dashboard = ({ onNavigateToOrders }) => {
  const [stats, setStats] = useState({
    totalOrdens: 0,
    ordensConcluidas: 0,
    totalValorServico: 0,
    totalValorMaterial: 0
  });
  const [dataSource, setDataSource] = useState('Local');
  const [apiStatus, setApiStatus] = useState('unknown');
  
  const [statusData, setStatusData] = useState([]);
  const [faturamentoData, setFaturamentoData] = useState([]);
  const [tipoServicoData, setTipoServicoData] = useState([]);
  const [timelineData, setTimelineData] = useState([]);

  useEffect(() => {
    // Primeiro renderiza com base nos dados locais
    renderLocalData();
    // Em seguida, atualiza em segundo plano com dados da API
    renderApiData();

    const onSync = () => {
      renderLocalData();
    };
    window.addEventListener('sync:orders-updated', onSync);
    return () => {
      window.removeEventListener('sync:orders-updated', onSync);
    };
  }, []);

  const renderLocalData = async () => {
    try {
      await dataService.loadData();
      const localStats = dataService.getStats();
      const statusStats = dataService.getStatusStats();
      const faturamentoStats = dataService.getFaturamentoStats();
      const tipoServicoStats = dataService.getTipoServicoStats();
      const timelineStats = dataService.getTimelineStats();
      setStats(localStats);
      setStatusData(statusStats);
      setFaturamentoData(faturamentoStats);
      setTipoServicoData(tipoServicoStats);
      setTimelineData(timelineStats);
      setDataSource('Local');
    } catch (e) {
      setDataSource('Local');
    }
  };

  const renderApiData = async () => {
    // Checar saúde da API e tentar obter estatísticas em segundo plano
    backendApiService.getHealth()
      .then(() => setApiStatus('online'))
      .catch(() => setApiStatus('offline'));
    try {
      const apiStats = await backendApiService.getStats();
      setStats(apiStats);
      setDataSource('API');
    } catch (e) {
      // mantém dados locais
    }
  };

  const handleStatusChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const index = elements[0].index;
      const clickedStatus = statusData[index].label;
      
      // Mapear o status para o filtro correto
      let statusFilter = '';
      if (clickedStatus.includes('Concluída')) {
        statusFilter = 'Concluída';
      } else if (clickedStatus.includes('Aguardando')) {
        statusFilter = 'Aguardando';
      } else if (clickedStatus.includes('Execução')) {
        statusFilter = 'Execução';
      } else if (clickedStatus.includes('Pendente')) {
        statusFilter = 'Pendente';
      }
      
      onNavigateToOrders({ status: statusFilter });
    }
  };

  

  const handleExportDashboard = () => {
    exportService.exportDashboardToPDF(
      stats,
      statusData,
      timelineData
    );
  };

  const [pdfContentChoice, setPdfContentChoice] = useState('3');
  const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'info' });

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem('dashboard_pdf_content_choice');
      if (saved === '1' || saved === '2' || saved === '3') {
        setPdfContentChoice(saved);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem('dashboard_pdf_content_choice', pdfContentChoice);
    } catch {}
  }, [pdfContentChoice]);

  const handleExportPDF = async () => {
    try {
      console.log('Iniciando exportação PDF do Dashboard...');
      console.log('Stats atuais:', stats);
      const includeList = pdfContentChoice === '1' || pdfContentChoice === '3';
      const includeCharts = pdfContentChoice === '2' || pdfContentChoice === '3';

      const currentFiltered = dataService.getFilteredData() || [];
      const ordersForReport = currentFiltered.map(o => ({
        id: o['Ord.Ser'] ?? '',
        cliente: dataService.safeValue(o['Cliente']),
        status: dataService.safeValue(o['Posição']),
        data: dataService.formatDate(o['Data Inclusao'])
      }));
      
      const dataToExport = {
        totalOrders: stats.totalOrdens,
        zaffariOrders: stats.zaffariOrders || 0,
        pendingOrders: stats.ordensPendentes || 0,
        completedOrders: stats.ordensConcluidas,
        orders: ordersForReport,
        statusData,
        faturamentoData,
        tipoServicoData,
        timelineData
      };
      
      console.log('Dados para exportação:', dataToExport);
      
      await pdfExportService.exportDashboardReport(dataToExport, { includeList, includeCharts });
      console.log('Exportação PDF concluída com sucesso!');
      setToast({ message: 'PDF gerado com sucesso! Verifique a pasta de downloads.', type: 'success' });
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      setToast({ message: 'Erro ao gerar PDF: ' + error.message, type: 'error' });
    }
  };

  return (
    <div className="space-y-6">
      {toast.message && (
        <UpdateNotification
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: 'info' })}
        />
      )}
      {/* Status de Fonte e API */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-dark-text-secondary">
          Fonte de dados: <span className="badge-info">{dataSource}</span>
        </div>
        <div className="text-sm text-dark-text-secondary">
          API: {apiStatus === 'online' ? (
            <span className="badge-success">Online</span>
          ) : apiStatus === 'offline' ? (
            <span className="badge-danger">Offline</span>
          ) : (
            <span className="badge-warning">Verificando...</span>
          )}
        </div>
      </div>
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <KPICard
          title="Total de Ordens"
          value={stats.totalOrdens.toLocaleString('pt-BR')}
          icon={DocumentTextIcon}
          color="primary"
        />
        <KPICard
          title="Ordens Concluídas"
          value={stats.ordensConcluidas.toLocaleString('pt-BR')}
          icon={CheckCircleIcon}
          color="success"
          subtitle={`${((stats.ordensConcluidas / stats.totalOrdens) * 100 || 0).toFixed(1)}% do total`}
        />
        <KPICard
          title="Valor Cobrado - Serviços"
          value={dataService.formatCurrency(stats.totalValorServico)}
          icon={WrenchScrewdriverIcon}
          color="primary"
        />
        
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        <div className="flex items-center gap-2 relative">
          <span className="text-sm text-dark-text-secondary">Conteúdo do PDF</span>
          <button
            aria-haspopup="true"
            aria-expanded={isPdfMenuOpen}
            onClick={() => setIsPdfMenuOpen(!isPdfMenuOpen)}
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            {pdfContentChoice === '1' && (
              <>
                <ListBulletIcon className="w-4 h-4" />
                Lista
              </>
            )}
            {pdfContentChoice === '2' && (
              <>
                <ChartBarIcon className="w-4 h-4" />
                Gráficos
              </>
            )}
            {pdfContentChoice === '3' && (
              <>
                <ListBulletIcon className="w-4 h-4" />
                <ChartBarIcon className="w-4 h-4" />
                Ambos
              </>
            )}
          </button>
          {isPdfMenuOpen && (
            <div
              role="menu"
              aria-label="Escolher conteúdo do PDF"
              className="absolute top-full left-0 mt-2 card-dark p-2 shadow-lg z-10 min-w-[180px]"
            >
              <button
                role="menuitem"
                onClick={() => { setPdfContentChoice('3'); setIsPdfMenuOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm hover:bg-gray-700 ${pdfContentChoice === '3' ? 'bg-gray-700' : ''}`}
              >
                <ListBulletIcon className="w-4 h-4" />
                <ChartBarIcon className="w-4 h-4" />
                Ambos
              </button>
              <button
                role="menuitem"
                onClick={() => { setPdfContentChoice('1'); setIsPdfMenuOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm hover:bg-gray-700 ${pdfContentChoice === '1' ? 'bg-gray-700' : ''}`}
              >
                <ListBulletIcon className="w-4 h-4" />
                Lista
              </button>
              <button
                role="menuitem"
                onClick={() => { setPdfContentChoice('2'); setIsPdfMenuOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm hover:bg-gray-700 ${pdfContentChoice === '2' ? 'bg-gray-700' : ''}`}
              >
                <ChartBarIcon className="w-4 h-4" />
                Gráficos
              </button>
            </div>
          )}
        </div>
        <button
          onClick={handleExportPDF}
          className="btn-primary flex items-center gap-2"
        >
          <DocumentArrowDownIcon className="w-5 h-5" />
          Exportar PDF
        </button>
        <button
          onClick={handleExportDashboard}
          className="btn-secondary flex items-center gap-2"
        >
          <DocumentArrowDownIcon className="w-5 h-5" />
          Exportar Excel
        </button>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Status Chart */}
        <ChartContainer title="Distribuição por Status">
          {statusData.length > 0 && (
            <PieChart 
              data={statusData} 
              onClick={handleStatusChartClick}
            />
          )}
        </ChartContainer>

        {/* Painéis laterais: Resumo por Status + Métricas Adicionais */}
        <div className="space-y-6">
          <div className="card-dark p-6">
            <h3 className="text-lg font-semibold text-dark-text mb-4 border-b border-gray-700 pb-2">
              Resumo por Status
            </h3>
            <div className="space-y-3">
              {statusData.slice(0, 5).map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-dark-text-secondary text-sm">
                    {item.label}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-dark-text">
                      {item.value}
                    </span>
                    <span className="text-xs text-dark-text-secondary">
                      ({((item.value / stats.totalOrdens) * 100).toFixed(1)}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card-dark p-6">
            <h3 className="text-lg font-semibold text-dark-text mb-4 border-b border-gray-700 pb-2">
              Métricas Adicionais
            </h3>
            <div className="space-y-4">
              <div>
                <div className="text-dark-text-secondary text-sm mb-1">
                  Taxa de Conclusão
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-success h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${((stats.ordensConcluidas / stats.totalOrdens) * 100) || 0}%` 
                      }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-dark-text">
                    {((stats.ordensConcluidas / stats.totalOrdens) * 100 || 0).toFixed(1)}%
                  </span>
                </div>
              </div>
              
              <div className="pt-2 border-t border-gray-700">
                <div className="text-dark-text-secondary text-sm mb-1">
                  Valor Total
                </div>
                <div className="text-lg font-semibold text-dark-text">
                  {dataService.formatCurrency(stats.totalValorServico + stats.totalValorMaterial)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        <ChartContainer title="Timeline de Ordens">
          {timelineData.length > 0 && (
            <LineChart data={timelineData} />
          )}
        </ChartContainer>
      </div>

      
    </div>
  );
};

export default Dashboard;
